/**
 * Recommended Products section — Oilogic Care
 *
 * Two custom elements, self-contained (no dependency on the Most Loved section):
 *
 * 1. <oilogic-product-recommendations> — lazily loads Shopify product
 *    recommendations through the Section Rendering API and swaps in the
 *    server-rendered slider markup. When the section already arrives with
 *    server-rendered content (the product has a custom.recommended_products
 *    metafield) no fetch happens.
 *
 * 2. <oilogic-recommended-slider> — Embla-powered product carousel (loop,
 *    centered) with prev/next controls and a progress bar. Embla loads once per
 *    page via the shared singleton loader (embla-loader.js). Progressive
 *    enhancement: without Embla the track is a native horizontal scroller.
 */

class OilogicProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.querySelector('oilogic-recommended-slider')) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        this._load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async _load() {
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('oilogic-product-recommendations');

      if (fresh && fresh.querySelector('oilogic-recommended-slider')) {
        this.innerHTML = fresh.innerHTML;
      } else {
        this.hidden = true;
      }
    } catch (error) {
      this.hidden = true;
    }
  }
}

customElements.define('oilogic-product-recommendations', OilogicProductRecommendations);

class OilogicRecommendedSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-recommended-viewport]');
    this.fill = this.querySelector('[data-recommended-progress]');
    this.prevBtn = this.querySelector('[data-recommended-prev]');
    this.nextBtn = this.querySelector('[data-recommended-next]');
    if (!this.viewport) return;

    if (typeof window.EmblaCarousel === 'function') {
      this._init();
    } else if (typeof window.oilogicEmbla === 'function') {
      window.oilogicEmbla(this.dataset.emblaUrl)
        .then(() => this._init())
        .catch(() => {});
    }
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    this.embla?.destroy();
    this.embla = null;
  }

  _init() {
    if (this.embla || typeof window.EmblaCarousel !== 'function' || !this.isConnected) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.embla = window.EmblaCarousel(this.viewport, {
      loop: true,
      align: 'center',
      dragFree: false,
      duration: prefersReducedMotion ? 0 : 25,
      // Mobile: start-align so two full cards show from the left edge (Figma
      // mobile). Desktop keeps center-align (3 cards + peek).
      breakpoints: {
        '(max-width: 989px)': { align: 'start' },
      },
    });

    this.prevBtn?.addEventListener('click', () => this.embla.scrollPrev(), { signal });
    this.nextBtn?.addEventListener('click', () => this.embla.scrollNext(), { signal });

    // :active alone is unreliable on hybrid touch+mouse devices (some never
    // fire it for touch). Pointer events give a consistent pressed state
    // across mouse, touch and pen.
    [this.prevBtn, this.nextBtn].forEach((btn) => {
      if (!btn) return;
      const press = () => btn.classList.add('is-pressed');
      const release = () => btn.classList.remove('is-pressed');
      btn.addEventListener('pointerdown', press, { signal });
      btn.addEventListener('pointerup', release, { signal });
      btn.addEventListener('pointercancel', release, { signal });
      btn.addEventListener('pointerleave', release, { signal });
    });

    this.embla.on('select', () => this._syncProgress());
    this.embla.on('reInit', () => this._syncProgress());
    this._syncProgress();
  }

  _syncProgress() {
    if (!this.fill || !this.embla) return;
    const snaps = this.embla.scrollSnapList().length;
    if (snaps < 2) return;
    const pct = Math.round((this.embla.selectedScrollSnap() / (snaps - 1)) * 100);
    this.fill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  }
}

customElements.define('oilogic-recommended-slider', OilogicRecommendedSlider);
